/**
 * Article template ESM (Phase 10).
 *
 * Replaces theme/js/templates/article.js. Drives the horizontal-scroll
 * article variant (style-profiles, lookbook, etc.) on
 * `theme/templates/article.liquid`. The longform/responsive variants
 * carry no JS — they're handled by CSS only.
 *
 * Behavior pinned by the preflight (`tests/static/article.test.js`):
 *
 *   - Acts only when `#article[data-template="horizontal"]` is on the
 *     page; bails on the smallMedium breakpoint and on already-init'd
 *     wrappers via the `.article-init` class.
 *
 *   - Sticky pin (Phase 7): the `.article-init` class triggers
 *     `position: sticky; top: 0` on `#article-content` via
 *     `_article-template-horizontal.scss`. We give `#article` a
 *     min-height of (content height + horizontal duration width) so the
 *     pin un-sticks at the right scrollY.
 *
 *   - rAF-throttled scroll listener: a single `requestAnimationFrame`
 *     drives `updateTranslateX`. Progress = clamp((scrollY - articleTop)
 *     / width, 0, 1); transform = `translateX(-width * progress)` on
 *     `.article-content-inner`.
 *
 *   - Mousewheel elastic-scroll guard: `preventDefault()` only at the
 *     extremes of the page (scrolling up at top, scrolling down at
 *     bottom). Listener is non-passive so preventDefault works.
 *
 *   - Section click navigation: smooth-scrolls the clicked
 *     `div[class^="section-"]` into view (centered for narrow sections,
 *     flush-left for sections wider than the viewport). A second click
 *     on an already-centered section advances to the next section, or
 *     scrolls past `#after` if there's no next.
 *
 *   - Image-load coordination: a small native `onImagesLoaded` helper
 *     debounces a recalc per image load and runs `calculateHorzDistance`
 *     once if every image is already cached.
 *
 *   - Reset on breakpoint: when smallMedium matches and the article is
 *     init'd, strips the inline transform/min-height, removes
 *     `.article-init`, detaches every listener, and cancels both rAF ids.
 *
 * Source path: theme/js/templates/article.esm.js
 * Built name : ./article.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-04
 * Model: Claude Opus 4.7
 *
 * [Article]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';

const ARTICLE_SELECTOR = '#article';
const CONTENT_SELECTOR = '#article-content';
const CONTENT_INNER_SELECTOR = '.article-content-inner';
const SECTION_SELECTOR = 'div[class^="section-"]';
const HORIZONTAL_TEMPLATE = 'horizontal';
const ARTICLE_INIT_CLASS = 'article-init';
const SECTION_TOLERANCE_PX = 20;

function isSmallMedium() {
  const bp = appGet('breakpoints.smallMedium', null);
  return Boolean(bp && bp.matches);
}

// Native equivalent of app.onImagesLoaded. Calls onLoad once per non-cached
// image as it finishes; calls onAllCached once if every image was already
// in the cache when measured. Returns a teardown function that detaches
// every listener it attached (used by the reset path).
function onImagesLoaded(images, { onLoad, onAllCached } = {}) {
  const cleanups = [];
  let pending = 0;
  for (const img of images) {
    if (img.complete && img.naturalHeight !== 0) continue;
    pending++;
    const handler = () => {
      pending--;
      if (typeof onLoad === 'function') onLoad();
    };
    img.addEventListener('load', handler);
    img.addEventListener('error', handler);
    cleanups.push(() => {
      img.removeEventListener('load', handler);
      img.removeEventListener('error', handler);
    });
  }
  if (pending === 0 && typeof onAllCached === 'function') {
    requestAnimationFrame(onAllCached);
  }
  return () => cleanups.forEach((fn) => fn());
}

function setupHorizontalArticle() {
  const article = document.querySelector(ARTICLE_SELECTOR);
  if (!article) return;
  if (isSmallMedium() || article.classList.contains(ARTICLE_INIT_CLASS)) return;

  const content = article.querySelector(CONTENT_SELECTOR);
  const scrollContents = article.querySelector(CONTENT_INNER_SELECTOR);
  if (!content || !scrollContents) return;

  article.classList.add(ARTICLE_INIT_CLASS);

  let width = getDurationWidth();
  let rafId = 0;
  let scrollRafId = 0;
  let detachImages = () => {};

  setArticleHeight();
  updateTranslateX();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.addEventListener('wheel', preventElasticScroll, { passive: false });
  scrollContents.addEventListener('click', onSectionClickDelegate);

  detachImages = onImagesLoaded(article.querySelectorAll('img'), {
    onLoad: debouncedRecalculate,
    onAllCached: calculateHorzDistance,
  });

  function getDurationWidth() {
    let total = 0;
    for (const child of scrollContents.children) {
      total += child.getBoundingClientRect().width;
    }
    scrollContents.style.width = total + 'px';
    return total - window.innerWidth;
  }

  function setArticleHeight() {
    const contentHeight = content.offsetHeight;
    article.style.minHeight = contentHeight + width + 'px';
  }

  function updateTranslateX() {
    const articleTop = article.getBoundingClientRect().top + window.pageYOffset;
    const raw = (window.pageYOffset - articleTop) / width;
    const progress = Math.min(1, Math.max(0, raw));
    const distance = -width * progress;
    scrollContents.style.transform = 'translateX(' + distance + 'px)';
  }

  function onScroll() {
    cancelAnimationFrame(scrollRafId);
    scrollRafId = requestAnimationFrame(updateTranslateX);
  }

  function debouncedRecalculate() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(calculateHorzDistance);
  }

  function calculateHorzDistance() {
    if (isSmallMedium() && article.classList.contains(ARTICLE_INIT_CLASS)) {
      resetHorzArticle();
    } else {
      width = getDurationWidth();
      setArticleHeight();
      updateTranslateX();
    }
  }

  function onResize() {
    debouncedRecalculate();
  }

  // wheel event semantics: deltaY < 0 means scrolling toward the top of
  // the document (the legacy `mousewheel` event used wheelDelta > 0 for
  // the same direction). The guard preserves the original IIFE behavior:
  // block the elastic bounce only when the user is at the top scrolling
  // up or at the bottom scrolling down.
  function preventElasticScroll(event) {
    const scrollTop = window.pageYOffset;
    const winHeight = window.innerHeight;
    const bodyHeight = Math.floor(document.body.offsetHeight);
    const atTopScrollingUp = scrollTop === 0 && event.deltaY < 0;
    const atBottomScrollingDown = scrollTop + winHeight === bodyHeight && event.deltaY >= 0;
    if (atTopScrollingUp || atBottomScrollingDown) {
      event.preventDefault();
    }
  }

  function onSectionClickDelegate(event) {
    const section = event.target.closest(SECTION_SELECTOR);
    if (!section || !scrollContents.contains(section)) return;
    onSectionClick(section);
  }

  function onSectionClick(section) {
    const sectionWidth = section.getBoundingClientRect().width;
    const winWidth = window.innerWidth;
    const offsetLeft = section.offsetLeft;
    const centeredScroll = offsetLeft + sectionWidth / 2 - winWidth / 2;
    const toScroll = sectionWidth > winWidth ? offsetLeft : centeredScroll;

    if (Math.abs(window.pageYOffset - toScroll) < SECTION_TOLERANCE_PX) {
      const next = section.nextElementSibling && section.nextElementSibling.matches(SECTION_SELECTOR)
        ? section.nextElementSibling
        : null;
      if (next) {
        next.click();
      } else {
        const after = document.getElementById('after');
        if (after) {
          window.scrollTo({
            top: after.offsetLeft + after.getBoundingClientRect().width - winWidth,
            behavior: 'smooth',
          });
        }
      }
    } else {
      window.scrollTo({ top: toScroll, behavior: 'smooth' });
    }
  }

  function resetHorzArticle() {
    scrollContents.removeAttribute('style');
    article.classList.remove(ARTICLE_INIT_CLASS);
    article.style.minHeight = '';
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('wheel', preventElasticScroll);
    scrollContents.removeEventListener('click', onSectionClickDelegate);
    cancelAnimationFrame(rafId);
    cancelAnimationFrame(scrollRafId);
    detachImages();
  }
}

function setupArticle() {
  const article = document.querySelector(ARTICLE_SELECTOR);
  if (!article) return;
  const template = article.getAttribute('data-template');
  if (template === HORIZONTAL_TEMPLATE) {
    setupHorizontalArticle();
  }
}

// Run at script eval (defer module — DOM is parsed), and on every Turbo
// navigation. Resize forwards to setupArticle so the sticky math
// recalculates / resets when crossing the smallMedium breakpoint.
setupArticle();
document.addEventListener('turbo:load', setupArticle);
window.addEventListener('resize', setupArticle);

log('[Article] registered');
